import React, { useEffect, useMemo, useState } from "react";
import {
    addWeeks,
    format,
    getISOWeek,
    getISOWeeksInYear,
    isSameISOWeek,
    startOfISOWeek,
    startOfISOWeekYear,
} from "date-fns";
import EquipmentService from "../../../services/Equipment.service";
import EquipmentCalendarGrid from "./EquipmentCalendarGrid";

export default function EquipmentWeeklyCalendar({
    equipmentTypeId,
    equipmentList,
    refreshSignal,
    onStartCheck,
    onOpenDetails,
    onResolveIssue,
}) {
    const today = new Date();

    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [inspections, setInspections] = useState([]);
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(false);

    const weeks = useMemo(() => {
        const midYear = new Date(currentYear, 6, 1);
        const yearStart = startOfISOWeekYear(midYear);
        const weekCount = getISOWeeksInYear(midYear);
        return Array.from({ length: weekCount }, (_, i) => addWeeks(yearStart, i));
    }, [currentYear]);

    useEffect(() => {
        if (!equipmentTypeId) return;
        setLoading(true);
        Promise.all([
            EquipmentService.listInspections({
                equipment_type: equipmentTypeId,
                year: currentYear,
            }),
            EquipmentService.listIssues({ equipment_type: equipmentTypeId, is_resolved: "false" }),
        ])
            .then(([inspRes, issueRes]) => {
                setInspections(Array.isArray(inspRes.data) ? inspRes.data : inspRes.data.results || []);
                setIssues(Array.isArray(issueRes.data) ? issueRes.data : issueRes.data.results || []);
            })
            .finally(() => setLoading(false));
    }, [equipmentTypeId, currentYear, refreshSignal]);

    const goBack = () => setCurrentYear((y) => y - 1);
    const goForward = () => setCurrentYear((y) => y + 1);

    const isCurrentYear = currentYear >= today.getFullYear();
    const currentWeekStart = startOfISOWeek(today);

    const columns = useMemo(
        () =>
            weeks.map((weekStart) => ({
                key: weekStart.toISOString(),
                label: `W${getISOWeek(weekStart)}`,
                isCurrent: isSameISOWeek(weekStart, today),
                isPast: weekStart.getTime() < currentWeekStart.getTime(),
                weekStart,
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [weeks]
    );

    const inspectionFor = (equipmentId, columnKey) =>
        inspections.find(
            (i) =>
                i.equipment === equipmentId &&
                isSameISOWeek(new Date(`${i.date}T00:00:00`), new Date(columnKey))
        );

    const openIssueFor = (equipmentId) => issues.find((i) => i.equipment === equipmentId) || null;

    const handleUncheck = async (inspection) => {
        setLoading(true);
        try {
            await EquipmentService.deleteInspection(inspection.id);
            setInspections((prev) => prev.filter((i) => i.id !== inspection.id));
        } finally {
            setLoading(false);
        }
    };

    return (
        <EquipmentCalendarGrid
            title={format(new Date(currentYear, 6, 1), "yyyy")}
            onPrev={goBack}
            onNext={goForward}
            nextDisabled={isCurrentYear}
            columns={columns}
            equipmentList={equipmentList}
            loading={loading}
            inspectionFor={inspectionFor}
            openIssueFor={openIssueFor}
            onStartCheck={onStartCheck}
            onOpenDetails={onOpenDetails}
            onResolveIssue={onResolveIssue}
            onUncheck={handleUncheck}
        />
    );
}
